import moment, { Moment } from 'moment/moment'
import { Workout, WorkoutPayload } from '@/core/models/workout'
import { Stroke } from '@/core/models/stroke'

// Grouped audience as delivered by the API; the model keeps it as a flat map internally.
export interface AudiencePayload {
    always_show_to?: string[]
    relevant_to?: string[]
    maybe_relevant_to?: string[]
    never_show_to?: string[]
}

export interface WorkoutCollectionPayload {
    id: number
    user_id?: number
    name: string
    description: string
    video_url: string
    mux_playback_id: string
    author: string
    photo_url: string
    header_photo_url: string
    level: string
    audience?: AudiencePayload
    workout_count: number
    sort_order: number
    enabled?: boolean
    archived?: boolean
    created_at: string
    strokes?: any[]
    workouts?: WorkoutPayload[]
    translations?: {}
}

export class WorkoutCollectionModel {

    id: number
    userId?: number
    name: string
    description: string
    videoUrl: string
    muxPlaybackId: string
    author: string
    photoUrl: string
    photo?: File
    headerPhotoUrl: string
    headerPhoto?: File
    level: string
    // Flat map { characteristic: attitude }; a missing key means "not set".
    audience: { [key: string]: string } = {}
    workoutCount: number
    sortOrder: number
    enabled: boolean
    archived: boolean
    createdAt: Moment
    strokeIds: number[] = []
    workouts: Workout[] = <Workout[]>[]
    translations?: {} = <any>{}

    constructor(data: WorkoutCollectionPayload) {
        this.fill(data)
    }

    fill(data: WorkoutCollectionPayload): this {
        this.id = data.id
        this.userId = data.user_id
        this.name = data.name
        this.description = data.description
        this.videoUrl = data.video_url
        this.muxPlaybackId = data.mux_playback_id
        this.author = data.author
        this.photoUrl = data.photo_url
        this.headerPhotoUrl = data.header_photo_url
        this.level = data.level
        this.audience = WorkoutCollectionModel.flattenAudience(data.audience)
        this.workoutCount = data.workout_count
        this.sortOrder = data.sort_order
        this.enabled = data.enabled === undefined ? true : !! data.enabled
        this.archived = !! data.archived
        this.createdAt = moment(data.created_at)
        this.capture(data)
        return this
    }

    capture(data: WorkoutCollectionPayload): this {
        if (data.translations) {
            this.translations = data.translations
        }
        if (data.strokes) {
            this.strokeIds = data.strokes.map((item: Stroke) => item.id)
        }
        if (data.workouts) {
            this.workouts = data.workouts.map((item) => new Workout(item))
        }
        return this
    }

    // { always_show_to: ['pregnant'] } -> { pregnant: 'always_show_to' }
    static flattenAudience(audience?: AudiencePayload): { [key: string]: string } {
        const flat = <{ [key: string]: string }>{}
        if (! audience) {
            return flat
        }
        Object.keys(audience).forEach((attitude) => {
            ((<any>audience)[attitude] || []).forEach((characteristic: string) => {
                flat[characteristic] = attitude
            })
        })
        return flat
    }

    exists(): boolean {
        return !! this.id
    }

    unset(): void {
        this.fill(<any>{})
        this.photo = undefined
        this.headerPhoto = undefined
        this.strokeIds = []
        this.workouts = <Workout[]>[]
        this.translations = {}
        this.audience = {}
    }

    addWorkouts(workouts: Workout[] = []): void {
        const existing = this.workouts.map(w => w.id)
        workouts.forEach((workout) => {
            if (! existing.includes(workout.id)) {
                this.workouts.push(workout)
                existing.push(workout.id)
            }
        })
    }

    moveWorkout(currentIndex: number, index: number): void {
        if (index < 0 || index >= this.workouts.length) {
            return
        }
        const [workout] = this.workouts.splice(currentIndex, 1)
        this.workouts.splice(index, 0, workout)
    }

    delWorkout(index: number): void {
        this.workouts.splice(index, 1)
    }

    __workouts(): any[] {
        return this.workouts.map(workout => ({ id: workout.id }))
    }

    payload(): WorkoutCollectionPayload | any {
        return {
            id: this.id,
            user_id: this.userId,
            name: this.name,
            description: this.description,
            video_url: this.videoUrl,
            mux_playback_id: this.muxPlaybackId,
            author: this.author,
            level: this.level,
            audience: this.audience,
            sort_order: this.sortOrder,
            enabled: this.enabled,
            archived: this.archived,
            stroke_ids: this.strokeIds,
            workouts: this.__workouts(),
            translations: this.translations,
        }
    }
}
